import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isAhlAvailable } from '../../core/api/endpoints';
import { leagueUrl, navigate, type League } from '../../core/league/league';
import { COUNTRIES } from '../../core/model/countries';
import { setSetting, settings } from '../../core/settings/settings';
import { useSetting } from '../../core/settings/useSetting';
import type { AppState } from '../../core/sync/syncEngine';
import { Popover } from '../common/Popover';
import { Segmented } from '../common/Segmented';
import { AppIcon } from './AppIcon';
import { ContactEmail } from './ContactEmail';
import { WhatsNew } from './WhatsNew';

const pad2 = (value: number) => String(value).padStart(2, '0');

// "03:41:12" in the device's time zone, or undefined for a missing time.
export function formatClockTime(value: number | string | undefined): string | undefined {
    if (value === undefined) {
        return undefined;
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return undefined;
    }
    return `${pad2(date.getHours())}:${pad2(date.getMinutes())}:${pad2(date.getSeconds())}`;
}

function LanguageToggle() {
    const { t } = useTranslation();
    const [language, setLanguage] = useSetting(settings.language);

    return (
        <div role="group" aria-label={t('header_language')} className="lang-toggle">
            <button type="button" lang="en" aria-pressed={language === 'en'} onClick={() => setLanguage('en')}>en</button>
            <span aria-hidden="true">|</span>
            <button type="button" lang="fi" aria-pressed={language === 'fi'} onClick={() => setLanguage('fi')}>fi</button>
        </div>
    );
}

// Asked once per page, when the menu is first opened on the NHL page.
let ahlAvailable: Promise<boolean> | undefined;

export function resetAhlAvailableForTests(): void {
    ahlAvailable = undefined;
}

// Whether to offer the league switch: always on the AHL page, on the NHL page
// once the backend has shown that it serves AHL data.
function useLeagueSwitchShown(league: League): boolean {
    const [available, setAvailable] = useState(league === 'AHL');
    useEffect(() => {
        if (league === 'AHL') {
            return undefined;
        }
        let active = true;
        ahlAvailable ??= isAhlAvailable();
        void ahlAvailable.then(value => {
            if (active) {
                setAvailable(value);
            }
        });
        return () => { active = false; };
    }, [league]);
    return available;
}

// The league is fixed for a page load: switching opens the other league's
// page (same tab route) and is remembered for the next visit to the front page.
const switchLeague = (next: League) => {
    setSetting(settings.league, next);
    navigate(leagueUrl(next, window.location.hash));
};

function MenuContent({ state, league, onWhatsNew }: { state: AppState; league: League; onWhatsNew(): void }) {
    const { t } = useTranslation();
    const [theme, setTheme] = useSetting(settings.theme);
    const updated = formatClockTime(state.lastUpdated);
    const leagueSwitchShown = useLeagueSwitchShown(league);

    return (
        <div className="menu">
            <div className="menu-row">
                <span>{t('menu_updated')}</span>
                <span className="mono muted" data-testid="updated-time">{updated ?? '–'}</span>
            </div>
            {state.refreshFailing &&
                <div className="menu-row menu-warning" role="status">{t('menu_refreshFailing')}</div>}
            {leagueSwitchShown &&
                <div className="menu-row">
                    <span>{t('menu_league')}</span>
                    <Segmented<League>
                        size="sm"
                        label={t('menu_league')}
                        value={league}
                        onChange={next => { if (next !== league) switchLeague(next); }}
                        options={[{ value: 'NHL', label: 'NHL' }, { value: 'AHL', label: 'AHL' }]}
                    />
                </div>}
            <div className="menu-row">
                <span>{t('menu_theme')}</span>
                <Segmented
                    size="sm"
                    label={t('menu_theme')}
                    value={theme}
                    onChange={setTheme}
                    options={[
                        { value: 'auto', label: t('theme_auto') },
                        { value: 'light', label: '☀', ariaLabel: t('theme_light') },
                        { value: 'dark', label: '☾', ariaLabel: t('theme_dark') },
                        { value: 'teletext', label: '235', ariaLabel: t('theme_teletext'), className: 'seg-teletext' },
                    ]}
                />
            </div>
            <button type="button" className="menu-row menu-button" onClick={onWhatsNew}>
                <span>{t('menu_whatsNew')}</span>
                <span className="muted" aria-hidden="true">›</span>
            </button>
            <div className="menu-row menu-feedback">
                <span>{t('menu_feedback')}</span>
                <ContactEmail />
            </div>
            <div className="menu-row">
                <span>{t('menu_version')}</span>
                <span className="mono muted" data-testid="app-version">v0.{state.appVersion ?? 0}</span>
            </div>
        </div>
    );
}

// App icon, country dropdown, language toggle and the menu with the settings
// that are rarely touched.
export function Header({ state, league }: { state: AppState; league: League }) {
    const { t, i18n } = useTranslation();
    const [country, setCountry] = useSetting(settings.country);
    // Alphabetical by the name shown, in the current language.
    const countryOptions = useMemo(
        () => COUNTRIES
            .map(option => ({ code: option.code, name: t(option.i18nKey) }))
            .sort((a, b) => a.name.localeCompare(b.name, i18n.language)),
        [t, i18n.language],
    );
    const [menuOpen, setMenuOpen] = useState(false);
    const menuButton = useRef<HTMLButtonElement>(null);
    const closeMenu = useCallback(() => setMenuOpen(false), []);
    const [whatsNewOpen, setWhatsNewOpen] = useState(false);
    const openWhatsNew = useCallback(() => {
        setMenuOpen(false);
        setWhatsNewOpen(true);
    }, []);
    // The menu row that opened it is gone, so focus returns to the menu button.
    const closeWhatsNew = useCallback(() => {
        setWhatsNewOpen(false);
        menuButton.current?.focus();
    }, []);

    return (
        <header className="app-header">
            <h1 className="visually-hidden">{t('app_name')}</h1>
            <AppIcon label={t('app_name')} />
            {league === 'AHL' && <span className="league-badge" data-testid="league-badge">AHL</span>}
            <label className="select-wrap">
                <span className="visually-hidden">{t('header_country')}</span>
                <select
                    className="country-select"
                    value={country}
                    onChange={event => setCountry(event.target.value as typeof country)}
                >
                    {countryOptions.map(option => (
                        <option key={option.code} value={option.code}>{option.name}</option>
                    ))}
                </select>
            </label>
            <LanguageToggle />
            <div className="popover-anchor">
                <button
                    ref={menuButton}
                    type="button"
                    className={`kebab${menuOpen ? ' on' : ''}`}
                    aria-label={t('menu_open')}
                    aria-haspopup="dialog"
                    aria-expanded={menuOpen}
                    onClick={() => setMenuOpen(open => !open)}
                >
                    <span aria-hidden="true">⋮</span>
                    {state.refreshFailing && <span className="kebab-dot" aria-hidden="true" />}
                </button>
                <Popover open={menuOpen} onClose={closeMenu} anchorRef={menuButton} label={t('menu_open')}>
                    <MenuContent state={state} league={league} onWhatsNew={openWhatsNew} />
                </Popover>
            </div>
            {whatsNewOpen && <WhatsNew onClose={closeWhatsNew} />}
        </header>
    );
}
